import moment from 'moment'
import React, { FC, useEffect } from 'react'
import { Control, UseFormRegister, UseFormReset, UseFormWatch } from 'react-hook-form'

import { UseFormSetValue, UseFormTrigger } from 'react-hook-form/dist/types/form'
import {
  FeesTypeItem,
  RegistrationFeesType,
} from '../../../../../models/event-fees/event-fees.interface'
import { EventFeesModel } from '../../../../../models/event-fees/event-fees.model'
import { selectedEvent } from '../../../../../store/events/eventsSlice'
import { useAppSelector } from '../../../../../store/hooks'
import {
  filteredParentSchedule,
  filteredSchedule,
  IFilters,
} from '../SchedulingScreenEventSchedulingFormComponent'
import { ScheduleItemByClass } from './ScheduleItemByClass'
import { ScheduleItemByRider } from './ScheduleItemByRider'
import { IRegistrationByDayInterface } from '../../../../../models/registrations-by-day/registrationByDay.interface'

interface EntriesSchedulesProps {
  handleModal: (showHide: boolean, typeOfModal: string, dataToPassOn?: any) => void
  locationOptions: { label: string; value: string }[]
  control: Control<any>
  watch: UseFormWatch<any>
  setValue: UseFormSetValue<any>
  errors: any
  register: UseFormRegister<any>
  trigger: UseFormTrigger<any>
  reset: UseFormReset<any>
  filters: IFilters
  searchValue: string
  scheduleBy: 'rider' | 'class'
  selectedIds: string[]
  onSelected: (id: string) => void
  isBatchSchedule: boolean
  registrationByDay: IRegistrationByDayInterface[]
}

const eventFeesFormDefaultValues = new EventFeesModel().toObject()

export const EntriesSchedules: FC<EntriesSchedulesProps> = ({
  handleModal,
  locationOptions,
  control,
  reset,
  trigger,
  register,
  watch,
  setValue,
  errors,
  filters,
  searchValue,
  scheduleBy,
  selectedIds,
  onSelected,
  isBatchSchedule,
  registrationByDay,
}) => {
  const selectedEventR = useAppSelector(selectedEvent)

  const areAllFieldsValid = (index: number) => {
    const children = watch(`registrationFees.${index}.children`)
    if (children?.[0]) {
      const nameIsValid = !!watch(`registrationFees.${index}.name`)?.trim()
      const isValidChildren = children?.filter((child: FeesTypeItem) => {
        const locationIsValid = !!child.location
        const nameIsValid = !!child.name?.trim()
        const noteIsValid = !!child.note?.trim()
        const startDateIsValid = moment(child.startDate).isValid()

        if (scheduleBy === 'class') {
          const tagIsValid = !!child.tag
          return nameIsValid && startDateIsValid && tagIsValid && locationIsValid && noteIsValid
        } else {
          // for rider scheduling - check increment instead of tag
          const incrementIsValid = !!child.increment
          return (
            nameIsValid && startDateIsValid && incrementIsValid && locationIsValid && noteIsValid
          )
        }
      })
      return nameIsValid && isValidChildren.length === children.length
    } else {
      const locationIsValid = !!watch(`registrationFees.${index}.location`)
      const nameIsValid = !!watch(`registrationFees.${index}.name`)?.trim()
      const noteIsValid = !!watch(`registrationFees.${index}.note`)?.trim()
      const startDateIsValid = moment(watch(`registrationFees.${index}.startDate`)).isValid()

      if (scheduleBy === 'class') {
        const tagIsValid = !!watch(`registrationFees.${index}.tag`)
        return nameIsValid && startDateIsValid && tagIsValid && locationIsValid && noteIsValid
      } else {
        // for rider scheduling - check increment instead of tag
        const incrementIsValid = !!watch(`registrationFees.${index}.increment`)
        return nameIsValid && startDateIsValid && incrementIsValid && locationIsValid && noteIsValid
      }
    }
  }

  useEffect(() => {
    if (selectedEventR.EventFees) reset(new EventFeesModel(selectedEventR.EventFees).toObject())
    else reset(eventFeesFormDefaultValues)
  }, [reset, selectedEventR.EventFees, trigger])

  // render the appropriate component based on scheduleBy mode
  const renderScheduleItem = (
    field: RegistrationFeesType,
    parentIndex: number,
    isChild: boolean = false,
    childIndex?: number
  ) => {
    const commonProps = {
      index: isChild ? childIndex! : parentIndex,
      handleModal,
      register,
      control,
      watch,
      trigger,
      setValue,
      areAllFieldsValid,
      locationOptions,
      hasChildren: !isChild && field.children && field.children.length > 0,
      selector: isChild
        ? (`registrationFees.${parentIndex}.children.${childIndex!}` as const)
        : (`registrationFees.${parentIndex}` as const),
      parentSelector: isChild ? (`registrationFees.${parentIndex}` as const) : undefined,
      errorSelector:
        isChild && childIndex !== undefined
          ? errors?.registrationFees?.[parentIndex]?.children?.[childIndex]
          : errors?.registrationFees?.[parentIndex],
      selectedIds,
      onSelected,
      isBatchSchedule,
      registrationByDay,
    }

    if (scheduleBy === 'class') {
      return <ScheduleItemByClass key={isChild ? childIndex : parentIndex} {...commonProps} />
    } else {
      return <ScheduleItemByRider key={isChild ? childIndex : parentIndex} {...commonProps} />
    }
  }

  return (
    <>
      {watch('registrationFees').map((field: RegistrationFeesType, parentIndex: number) =>
        (
          field?.children?.[0]
            ? filteredParentSchedule({ searchValue, filters, field, registrationByDay })
            : filteredSchedule({ searchValue, filters, field, registrationByDay })
        ) ? (
          <>
            {/* Render parent item */}
            {renderScheduleItem(field, parentIndex)}

            {/* Render children if they exist */}
            {field.children?.[0] &&
              field.children.map((children, childIndex) =>
                filteredSchedule({ searchValue, filters, field: children, registrationByDay }) ? (
                  <div key={childIndex} className="pl-8">
                    {renderScheduleItem(field, parentIndex, true, childIndex)}
                  </div>
                ) : null
              )}
          </>
        ) : null
      )}
    </>
  )
}
